import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, 
  FileText, 
  Settings, 
  LogOut,
  Menu,
  X,
  Book,
  Users,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Ticket,
  Clock,
  UserPlus,
  Building2,
  Globe,
  FolderOpen,
  Calendar,
  BookOpen,
  Megaphone
} from 'lucide-react';
import { useAuth } from '../../admin/auth/AuthContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_client-dash-revamp/artifacts/wn2edy78_logo_transparent_background.png';

export default function AdminLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, state } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Auto-close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/admin-panel/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Menu sections with grouped items
  const menuSections = [
    {
      title: 'Overview',
      items: [
        { path: '/admin-panel/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      ]
    },
    {
      title: 'Business',
      items: [
        { path: '/admin-panel/clients', icon: Building2, label: 'Clients' },
        { path: '/admin-panel/leads', icon: UserPlus, label: 'Leads' },
        { path: '/admin-panel/performance', icon: TrendingUp, label: 'Performance' },
      ]
    },
    {
      title: 'Staff Operations',
      items: [
        { path: '/admin-panel/bookings', icon: Calendar, label: 'Booking Manager' },
        { path: '/admin-panel/sops', icon: BookOpen, label: 'SOP Manager' },
        { path: '/admin-panel/announcements', icon: Megaphone, label: 'Announcements' },
        { path: '/admin-panel/support-tickets', icon: Ticket, label: 'Support Tickets' },
        { path: '/admin-panel/time-entries', icon: Clock, label: 'Time Entries' },
      ]
    },
    {
      title: 'Website Content',
      items: [
        { path: '/admin-panel/homepage-content', icon: Globe, label: 'Homepage' },
        { path: '/admin-panel/case-studies', icon: Book, label: 'Case Studies' },
      ]
    },
    {
      title: 'System',
      items: [
        { path: '/admin-panel/settings', icon: Settings, label: 'Settings' },
      ]
    }
  ];

  const NavItem = ({ item, collapsed }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    
    return (
      <Link
        to={item.path}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
          isActive 
            ? 'bg-blue-600 text-white' 
            : 'text-slate-600 hover:bg-slate-100'
        }`}
      >
        <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-white' : 'text-slate-400'}`} />
        {!collapsed && (
          <span className="font-medium text-sm">{item.label}</span>
        )}
      </Link>
    );
  };

  const Sidebar = ({ mobile = false, collapsed = false }) => (
    <div className={`flex flex-col h-full ${mobile ? '' : ''}`}>
      {/* Logo */}
      <div className={`p-4 border-b border-slate-200 ${collapsed ? 'flex justify-center' : ''}`}>
        <Link to="/admin-panel/dashboard" className="flex items-center gap-3">
          <img 
            src={LOGO_URL} 
            alt="BirdenMedia" 
            className={`${collapsed ? 'h-8 w-8 object-contain' : 'h-8'}`}
          />
          {!collapsed && (
            <div>
              <p className="font-bold text-slate-800 text-sm">BirdenMedia</p>
              <p className="text-xs text-slate-500">Admin Panel</p>
            </div>
          )}
        </Link>
      </div>

      {/* Menu Sections */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-4">
        {menuSections.map((section) => (
          <div key={section.title}>
            {!collapsed && (
              <p className="px-3 mb-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                {section.title}
              </p>
            )}
            <div className="space-y-1">
              {section.items.map((item) => (
                <NavItem key={item.path} item={item} collapsed={collapsed} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* User & Logout */}
      <div className={`p-3 border-t border-slate-200 ${collapsed ? 'flex flex-col items-center' : ''}`}>
        {!collapsed && state?.username && (
          <div className="px-3 py-2 mb-2">
            <p className="text-sm font-medium text-slate-700">{state.username}</p>
            <p className="text-xs text-slate-500">Administrator</p>
          </div>
        )}
        <button
          onClick={handleLogout}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-600 hover:bg-red-50 transition-colors w-full ${collapsed ? 'justify-center' : ''}`}
        >
          <LogOut className="w-5 h-5" />
          {!collapsed && <span className="font-medium text-sm">Logout</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside 
        className={`hidden lg:block fixed left-0 top-0 h-screen bg-white border-r border-slate-200 z-40 transition-all duration-300 ${
          sidebarCollapsed ? 'w-20' : 'w-64'
        }`}
      >
        <Sidebar collapsed={sidebarCollapsed} />
        
        {/* Collapse Toggle */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="absolute -right-3 top-20 w-6 h-6 bg-white border border-slate-200 rounded-full flex items-center justify-center shadow-sm hover:bg-slate-50"
        >
          {sidebarCollapsed ? (
            <ChevronRight className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronLeft className="w-4 h-4 text-slate-400" />
          )}
        </button>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-4">
        <Link to="/admin-panel/dashboard" className="flex items-center gap-2">
          <img src={LOGO_URL} alt="BirdenMedia" className="h-8" />
          <span className="font-bold text-slate-800">Admin</span>
        </Link>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 hover:bg-slate-100 rounded-lg"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </header>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', damping: 25 }}
              className="lg:hidden fixed left-0 top-0 w-72 h-screen bg-white z-50"
            >
              <Sidebar mobile />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main 
        className={`transition-all duration-300 ${
          sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'
        } pt-16 lg:pt-0 min-h-screen`}
      >
        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
